import React, { useState } from "react";
import { SlidersHorizontal, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const Num = ({ label, testid, value, onChange, step = 1 }) => (
  <label className="block">
    <span className="text-xs font-medium text-brand-stone">{label}</span>
    <input data-testid={testid} type="number" step={step} min="0" value={value} placeholder="—"
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-lg border border-brand-hairline bg-white px-3 py-2.5 font-mono text-sm text-brand-ink outline-none focus:ring-1 focus:ring-brand-emerald" />
  </label>
);

export default function SpecRefine({ property, address, unit, onRefined, onReset, isAdjusted }) {
  const [sqft, setSqft] = useState("");
  const [beds, setBeds] = useState("");
  const [baths, setBaths] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!sqft && !beds && !baths) { toast.error("Enter at least one updated spec."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/property/refine", {
        address, unit: unit || null,
        square_footage: sqft ? Number(sqft) : null,
        bedrooms: beds ? Number(beds) : null,
        bathrooms: baths ? Number(baths) : null,
      });
      if (!data.ok) { toast.error(data.error?.message || "Could not recalculate."); return; }
      onRefined(data.property);
      toast.success("Estimate recalculated with your updated specs.");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Could not recalculate.");
    } finally { setLoading(false); }
  };

  return (
    <div className="rounded-2xl border border-brand-hairline bg-white p-6 sm:p-8 shadow-[0_4px_20px_rgba(11,30,140,0.04)]" data-testid="spec-refine-card">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-emeraldLight"><SlidersHorizontal className="h-4 w-4 text-brand-emerald" /></span>
        <span className="eyebrow">Refine Your Specs</span>
      </div>
      <h2 className="mt-2 font-serif text-2xl text-brand-ink">County records out of date?</h2>
      <p className="mt-2 text-sm text-brand-stone max-w-2xl">
        Records show <strong>{property.living_area_sqft?.toLocaleString()} sq ft · {property.beds} bd · {property.baths} ba</strong>. If you've added permitted living space or rooms, enter the corrected figures and we'll re-run the RentCast valuation and the AI panel on your specs.
      </p>
      <form onSubmit={submit} className="mt-5 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end max-w-2xl">
        <Num label="Living area (sq ft)" testid="refine-sqft-input" value={sqft} onChange={setSqft} />
        <Num label="Bedrooms" testid="refine-beds-input" value={beds} onChange={setBeds} />
        <Num label="Bathrooms" testid="refine-baths-input" value={baths} onChange={setBaths} step={0.5} />
        <button type="submit" disabled={loading} data-testid="refine-submit-button"
          className="h-[42px] rounded-lg bg-brand-emerald px-5 text-sm font-semibold text-brand-cream hover:bg-brand-emeraldHover transition-colors disabled:opacity-60 flex items-center gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{loading ? "Recalculating…" : "Recalculate"}
        </button>
      </form>
      {isAdjusted && (
        <button onClick={onReset} data-testid="refine-reset-button" className="mt-3 inline-flex items-center gap-1 text-xs text-brand-stone hover:text-brand-emerald">
          <RotateCcw className="h-3.5 w-3.5" /> Reset to county-record estimate
        </button>
      )}
      <p className="mt-4 text-[0.7rem] uppercase tracking-wider text-brand-stone/70">Owner-provided specs are not verified against permits or county records</p>
    </div>
  );
}
